import type { InterestAssetMatchScore, ParsedAsset } from '@aikoa/common'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { Pressable as Slot_Pressable, View as Slot_View } from '@rn-primitives/slot'
import type { ImageProps } from 'expo-image'
import { memo, useMemo } from 'react'
import { Pressable, View } from 'react-native'
import {
  collectAssetDetails,
  formatPropertyPrice,
  useStats,
  type BaseProps,
} from '~/components/assets/propertyUtils'
import { PropertyPriceAction } from '~/components/assets/PropertyPriceAction'
import { InterestMatchBadge } from '~/components/interests/InterestMatchBadge'
import Image from '~/components/layout/Image'
import { ValueList, type ValueItem } from '~/components/layout/ValueList.tsx'
import { Heading3 } from '~/components/typography/Titles.tsx'
import { FakeButton } from '~/components/ui/button'
import { Skeleton } from '~/components/ui/skeleton.tsx'
import { useUserId } from '~/components/user/SessionProvider'
import { getRandomBackgroundImage } from '~/lib/themeImages'
import { cn } from '~/lib/utils'
import { PropertyStats } from './PropertyStats'

export function PropertyCard({
  asset,
  className,
  isLoading,
  display = 'distinct',
  showAddPrice = false,
  onPriceAdded,
}: BaseProps) {
  const { addressTitle, areaString, askingPrice } = useMemo(
    () => collectAssetDetails(asset),
    [asset]
  )

  const stats = useStats(asset, display === 'inline')

  const statsRow = (
    <PropertyStats
      stats={stats}
      variant={'card'}
      textClassName={'text-foreground'}
      itemClassName={cn('w-auto')}
      className={cn('border-b border-border px-5 pb-3 pt-1.5', display === 'inline' && 'px-8')}
    />
  )

  const { t } = useTranslations('property')
  const language = useCurrentLanguage()

  const userId = useUserId()
  const isOwner = useMemo(
    () => !!userId && asset.relations.some((r) => r.user_id === userId),
    [userId, asset.relations]
  )

  const showPriceButton = isOwner && showAddPrice
  const relationType = asset.relations[0]?.relation_type

  const priceType = useMemo(() => {
    let priceType = askingPrice?.price_type

    if (!priceType) {
      priceType =
        relationType === 'prospect'
          ? 'estimated_price'
          : relationType === 'representation'
            ? 'asking_price'
            : 'final_price'
    }

    return priceType
  }, [askingPrice, relationType])

  const cardContent = (
    <View className="flex-1">
      {statsRow}
      <ValueList
        itemClassName={cn(display === 'inline' && 'px-6 pl-7 last:border-none')}
        values={
          [
            {
              icon: 'location-outline',
              value: addressTitle || asset.name,
              description: areaString,
            },
            (askingPrice || showPriceButton) && {
              icon: 'pricetag-outline',
              contentClassName: cn(askingPrice && 'relative flex-row flex-wrap'),
              labelClassName: cn(askingPrice && 'flex-1 self-center'),
              valueClassName: cn(askingPrice && 'order-last basis-full'),
              value: askingPrice
                ? formatPropertyPrice(askingPrice.price, askingPrice.currency, language)
                : undefined,
              description:
                askingPrice &&
                t(($) => $.priceTypes[priceType as keyof typeof $.priceTypes], {
                  defaultValue: t(($) => $.priceTypes.default),
                }),
              children: showPriceButton ? (
                <PropertyPriceAction
                  asset={asset}
                  onSuccess={onPriceAdded}
                  triggerClassName={cn(!askingPrice ? 'self-start' : 'z-10 -mb-3')}
                />
              ) : null,
            },
          ].filter(Boolean) as ValueItem[]
        }
      />
    </View>
  )

  const media = (
    <View
      className={cn('relative w-full overflow-hidden p-2', display === 'inline' && 'px-6 pt-0')}>
      <PropertyCardImage
        asset={asset}
        style={useMemo(
          () => ({
            width: '100%',
            height: 'auto',
            aspectRatio: 16 / 8,
            borderRadius: 7,
            zIndex: 1,
          }),
          []
        )}
      />
    </View>
  )

  const content = (
    <View
      className={cn(
        'flex-1',
        display === 'distinct' && 'overflow-hidden rounded-xl bg-card shadow-md',
        display === 'inline' && 'shadow-none',
        className
      )}>
      {media}
      {cardContent}
    </View>
  )

  if (isLoading) {
    return <Skeleton className="flex-1">{content}</Skeleton>
  }

  return <Slot_View>{content}</Slot_View>
}

export default PropertyCard

export const PropertyCardImage = memo(
  function PropertyCardImage({ asset, ...imageProps }: { asset: ParsedAsset } & ImageProps) {
    const mediaUrl = asset?.media?.[0]?.url

    const imageSource = useMemo(() => {
      if (!mediaUrl) return getRandomBackgroundImage()
      return { uri: mediaUrl }
    }, [mediaUrl])

    return (
      <Image
        source={imageSource}
        contentFit="cover"
        cachePolicy="disk"
        transition={0}
        style={imageProps.style}
        errorClassName="p-0"
      />
    )
  },
  (prev, next) => {
    const prevUrl = prev.asset.media?.[0]?.url
    const nextUrl = next.asset.media?.[0]?.url
    return prevUrl === nextUrl && prev.style === next.style
  }
)

type OptionProps = BaseProps & {
  onPress?: () => void
  buttonLabel: string
  isSelected?: boolean
  isDisabled?: boolean
  matchScores?: InterestAssetMatchScore | null
}

export function PropertyOption({
  asset,
  className,
  onPress,
  buttonLabel,
  isSelected,
  isLoading,
  isDisabled,
  matchScores,
}: OptionProps) {
  const { t } = useTranslations('property')
  const language = useCurrentLanguage()

  const { addressTitle, askingPrice } = useMemo(() => collectAssetDetails(asset), [asset])

  const rowContent = (
    <View className="relative inset-auto flex-1 items-start justify-between gap-4 px-4 py-4">
      <View className="w-full flex-1 flex-row items-center justify-between gap-x-2 gap-y-1 self-stretch">
        <Heading3 className="flex-1 leading-none">{addressTitle || asset.name}</Heading3>
        {!!askingPrice && (
          <View className="ml-auto items-end">
            <Heading3 className="text-right font-bold leading-none">
              {askingPrice.price_type === 'estimated_price' ? '≈ ' : ''}
              {formatPropertyPrice(askingPrice.price, askingPrice.currency, language)}
            </Heading3>
          </View>
        )}
      </View>
      <View className="w-full flex-row items-center justify-between gap-6">
        <FakeButton variant={isSelected ? 'default' : 'outline'} size="sm" disabled={isDisabled}>
          {isSelected ? t(($) => $.actions.selected) : buttonLabel}
        </FakeButton>
        {!!matchScores && (
          <InterestMatchBadge scores={matchScores} breakdownClassName="items-end right-0" />
        )}
      </View>
    </View>
  )

  const content = (
    <Pressable
      disabled={isLoading || isDisabled}
      className={cn(
        'native:shadow-card mx-2 my-1 flex-1 rounded-xl bg-card shadow-md shadow-primary/20',
        'web:hover:bg-muted active:bg-muted',
        className
      )}>
      <View className={cn('flex-1 flex-row')}>
        <View className={cn('relative w-[20%] shrink-0 overflow-hidden rounded-lg p-1.5')}>
          <PropertyCardImage
            asset={asset}
            style={useMemo(
              () => ({
                width: 'auto',
                height: '100%',
                aspectRatio: 1 / 1,
                borderRadius: 7,
                zIndex: 1,
              }),
              []
            )}
          />
        </View>
        {rowContent}
      </View>
    </Pressable>
  )

  if (isLoading) {
    return <Skeleton className="flex-1">{content}</Skeleton>
  }

  return (
    <Slot_Pressable onPress={onPress} disabled={isDisabled}>
      {content}
    </Slot_Pressable>
  )
}
